import React, { FC } from 'react'

import { toast } from '@inno-ui/toast'
import { useForm, useStore } from '@tanstack/react-form'
import { AxiosError } from 'axios'
import {
  AuthButton,
  AuthButtonColor,
  AuthButtonVariant,
  AuthInput
} from 'modules/auth/components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ErrorResponse } from 'shared/api/auth/auth.types'
import { usePasswordRecoveryConfirmMutation } from 'shared/api/auth/requests'
import { PasswordRequirements } from 'shared/components/PasswordRequirements'
import { getFieldError } from 'shared/utils'

import styles from './SetNewPasswordForm.module.scss'
import { setNewPasswordFormSchema } from './SetNewPasswordForm.schema'
import { SetNewPasswordFormProps } from './SetNewPasswordForm.types'

export const SetNewPasswordForm: FC<SetNewPasswordFormProps> = ({ token }) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'forgot_password.set_new_password_form'
  })
  const navigate = useNavigate()

  const form = useForm({
    defaultValues: {
      password: '',
      password_confirmation: ''
    },
    validators: {
      onChange: setNewPasswordFormSchema,
      onMount: setNewPasswordFormSchema
    },
    onSubmit: ({ value }) => {
      passwordRecoveryConfirmMutation.mutate({
        token,
        password: value.password,
        password_confirmation: value.password_confirmation
      })
    }
  })

  const canSubmit = useStore(form.store, state => state.canSubmit)
  const isSubmitted = useStore(
    form.store,
    state => state.submissionAttempts > 0
  )

  const passwordRecoveryConfirmMutation = usePasswordRecoveryConfirmMutation({
    onSuccess: () => {
      navigate('/login')
    },
    onError: error => {
      const axiosError = error as AxiosError<ErrorResponse>
      const responseError = Object.values(
        axiosError.response?.data?.error ?? {}
      )
        .flat()
        .at(0)

      toast.error({
        title: t('error.title'),
        subTitle: responseError ?? t('error.subTitle')
      })
    }
  })

  return (
    <form
      className={styles.setNewPasswordForm}
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.Field name='password'>
        {field => (
          <>
            <AuthInput
              placeholder={t('password_placeholder')}
              type='password'
              name='password'
              value={field.state.value}
              onChange={e => {
                field.handleChange(e.target.value)
                if (isSubmitted) {
                  field.validate('change')
                }
              }}
              onBlur={field.handleBlur}
              error={getFieldError(field, {
                isSubmitted,
                requireTouched: true
              })}
            />
            <PasswordRequirements password={field.state.value} />
          </>
        )}
      </form.Field>
      <form.Field name='password_confirmation'>
        {field => (
          <AuthInput
            placeholder={t('password_confirmation_placeholder')}
            type='password'
            name='password_confirmation'
            value={field.state.value}
            onChange={e => {
              field.handleChange(e.target.value)
              if (isSubmitted) {
                field.validate('change')
              }
            }}
            onBlur={field.handleBlur}
            error={getFieldError(field, { isSubmitted, requireTouched: true })}
          />
        )}
      </form.Field>
      <AuthButton
        label={t('submit_button')}
        variant={AuthButtonVariant.FILLED_BLUE}
        colorType={AuthButtonColor.GHOST_DARKER}
        fullWidth={true}
        isBold
        disabled={!canSubmit}
        type='submit'
      />
    </form>
  )
}
